/**
 * GA4 (gtag.js) wrapper.
 *
 * The tag itself is loaded in index.html with Consent Mode v2 defaulting every
 * storage type to denied. Everything here is the other half of that deal: the
 * cookie banner decides when analytics_storage flips to granted, and page views
 * are sent by hand because a single-page app has only one document load.
 *
 * Nothing in here throws if the tag failed to load (ad blocker, offline build,
 * a test run) — the calls become no-ops instead.
 */

import { env } from '@/config/env';

/**
 * Empty when the build has no measurement id -- staging, and local development.
 * The tag is not loaded at all in that case (see index.html), so every call
 * below becomes a no-op rather than pushing into a dataLayer nobody reads.
 */
export const GA_MEASUREMENT_ID = env.gaMeasurementId;

/** Same key the cookie banner writes; read here to restore an earlier choice. */
const CONSENT_STORAGE_KEY = 'weareqa-cookie-consent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function gtag(...args: unknown[]): void {
  if (!GA_MEASUREMENT_ID) return;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag(...args);
}

/**
 * Tell the tag whether analytics cookies are allowed.
 *
 * Called on load with the stored answer and again whenever the banner is used,
 * so revoking consent takes effect without a reload.
 */
export function setAnalyticsConsent(granted: boolean): void {
  gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  });
}

/**
 * Marketing cookies map to the ad_* signals: ad_storage for the Ads cookies,
 * ad_user_data for enhanced conversions, ad_personalization for remarketing.
 * The Ads destination only loads when the build has VITE_GOOGLE_ADS_ID, but the
 * answer is passed on regardless, so enabling it later cannot silently ignore a
 * preference a visitor stored months ago.
 */
export function setMarketingConsent(granted: boolean): void {
  const value = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

type StoredPreferences = {
  necessary?: boolean;
  functional?: boolean;
  analytics?: boolean;
  marketing?: boolean;
};

/** The banner's stored preferences, or null if it has never been answered. */
export function readStoredConsent(): StoredPreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { preferences?: StoredPreferences };
    return parsed?.preferences ?? null;
  } catch {
    // Malformed or unavailable storage is treated as "never answered".
    return null;
  }
}

/**
 * Replay a previously stored choice into the tag on page load.
 *
 * Without this the Consent Mode defaults would win on every visit and a
 * returning visitor who accepted months ago would never be measured.
 */
export function syncStoredConsent(): void {
  const stored = readStoredConsent();
  if (!stored) return;
  setAnalyticsConsent(Boolean(stored.analytics));
  setMarketingConsent(Boolean(stored.marketing));
}

/** One page view. Title is read from the document, which components/SeoHead.tsx sets. */
export function trackPageView(path: string): void {
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/** A named event, for conversions worth reporting (form submits, call bookings). */
export function trackEvent(
  name: string,
  params: Record<string, unknown> = {},
): void {
  gtag('event', name, params);
}

/**
 * A Google Ads conversion, when the build carries both an Ads id and a label;
 * a no-op otherwise. Keyed by the submission id, so the same lead cannot be
 * counted twice.
 */
export function trackAdsConversion(transactionId: string): void {
  if (!env.googleAdsId || !env.googleAdsLeadLabel) return;
  gtag('event', 'conversion', {
    send_to: `${env.googleAdsId}/${env.googleAdsLeadLabel}`,
    transaction_id: transactionId,
  });
}

/**
 * The lead's email for Google's enhanced conversions, via gtag's own
 * `user_data`: gtag normalises and SHA-256-hashes it before anything leaves the
 * browser, which is Google's supported mechanism. Nothing here hashes by hand.
 *
 * Off unless two things are both true:
 *   1. The build sets VITE_ENHANCED_CONVERSIONS=true -- which should wait until
 *      the Privacy Policy says hashed contact data is shared with Google for ad
 *      measurement, and "enhanced conversions" is switched on in Google Ads (or
 *      "user-provided data collection" in GA4).
 *   2. The visitor accepted marketing cookies. gtag already withholds
 *      user-provided data while ad_user_data is denied, so this is a second,
 *      explicit gate rather than the only one -- a gap between the two must
 *      fail closed.
 */
export function setEnhancedConversionData(email: string): void {
  if (!env.enhancedConversions) return;
  if (!readStoredConsent()?.marketing) return;
  const normalized = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return;
  gtag('set', 'user_data', { email: normalized });
}
